<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells :indent="false">
        <h1 class="title">{{ group_detail.name }} ({{ group_detail.pax }}) ID: {{ $route.params.id }}</h1>
        <div class="status-title" :style="{ 'backgroundColor': group_detail.type.color || '#5f5f5f' }">
          {{ group_detail.type.name }}
        </div>
        <div v-if="group_detail.water_for_cooking" class="status-tag" style="background-color: #77b017">Bottled water</div>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Route:</span><span>{{ group_detail.route.name }} {{ group_detail.route.duration }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Tour start:</span><span>{{ dateFormat(group_detail.tour_start) }}</span>
            </div>
            <div class="info-cards__row-item">
              <span>Tour finish:</span><span>{{ dateFormat(group_detail.tour_end) }}</span>
            </div>
          </div>
        </div>
      </div>
      <app-cells><h2 class="title">Allocated beds</h2></app-cells>
      <v-client-table
        :data="table_allocated.items"
        :columns="table_allocated.columns"
        :options="table_allocated.options"
        class="table-default"
        :class="{ 'table-default--loading': table_allocated_preloader }"
      >
        <template #h__hotel><div class="table-default__left">Hotel</div></template>
        <template #h__clients><div class="table-default__left">Clients</div></template>
        <template #h__created_by><div class="table-default__left">Created by</div></template>
        <template #h__verified_by><div class="table-default__left">Verified by</div></template>
        <template #hotel="{ row }"><div class="table-default__left">{{ row.hotel.name }}</div></template>
        <template #check_in="{ row }">{{ dateFormat(row.check_in) }}</template>
        <template #check_out="{ row }">{{ dateFormat(row.check_out) }}</template>
        <template #clients="{ row }">
          <div class="table-default__left table-default__controls">
          <span
            :class="`fflag ff-md fflag-${row.first_room.clients[0].citizenship ? row.first_room.clients[0].citizenship.code : 'empty'}`"
            v-tooltip="{ content: row.first_room.clients[0].citizenship ? row.first_room.clients[0].citizenship.name : 'Not provided', placement: 'top-start', offset: 10 }"
          ></span>
            <img v-if="row.first_room.clients[0].gender?.code === 'male'" src="@/assets/img/male-icon.svg" alt="">
            <img v-if="row.first_room.clients[0].gender?.code === 'female'" src="@/assets/img/female-icon.svg" alt="">
            <img v-if="!row.first_room.clients[0].gender" src="@/assets/img/gender-plug-icon.svg" alt="">
            <span>{{ row.first_room.clients[0].full_name }}</span>
            <span v-if="row.first_room.clients[0].age">({{ row.first_room.clients[0].age }} y.o.)</span>
            |
            <span
              :class="`fflag ff-md fflag-${row.second_room.clients[0].citizenship ? row.second_room.clients[0].citizenship.code : 'empty'}`"
              v-tooltip="{ content: row.second_room.clients[0].citizenship ? row.second_room.clients[0].citizenship.name : 'Not provided', placement: 'top-start', offset: 10 }"
            ></span>
            <img v-if="row.second_room.clients[0].gender?.code === 'male'" src="@/assets/img/male-icon.svg" alt="">
            <img v-if="row.second_room.clients[0].gender?.code === 'female'" src="@/assets/img/female-icon.svg" alt="">
            <img v-if="!row.second_room.clients[0].gender" src="@/assets/img/gender-plug-icon.svg" alt="">
            <span>{{ row.second_room.clients[0].full_name }}</span>
            <span v-if="row.second_room.clients[0].age">({{ row.second_room.clients[0].age }} y.o.)</span>
          </div>
        </template>
        <template #created_by="{ row }">
          <div class="table-default__controls table-default__left">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.created_by?.crop?.crop_url)" alt="Avatar"
                v-tooltip="{ content: `${row.created_by?.full_name || ''}`, placement: 'top-start', offset: 10 }"
              >
            </div>
            {{ dateTimeFormat(row.created_at) }}
          </div>
        </template>
        <template #verified="{ row }">
          <app-checkbox
            v-if="!table_allocated_preloader"
            @change.native="onUpdateVerified(row)"
            v-model="row.verified"
            class="checkbox--table"
          />
        </template>
        <template #verified_by="{ row }">
          <div class="table-default__controls table-default__left">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.verified_by?.crop?.crop_url)" alt="Avatar"
                v-tooltip="{ content: `${row.verified_by?.full_name || ''}`, placement: 'top-start', offset: 10 }"
              >
            </div>
            {{ dateTimeFormat(row.verified_at) }}
          </div>
        </template>
      </v-client-table>
      <app-cells><h2 class="title">Before</h2></app-cells>
      <v-client-table
        :data="table_before.items"
        :columns="table_before.columns"
        :options="table_before.options"
        class="table-default"
      >
        <template #h__hotel><div class="table-default__left">Hotel</div></template>
        <template #h__name><div class="table-default__left">Full name</div></template>
        <template #h__group><div class="table-default__left">Group</div></template>
        <template #select="{ row }">
          <label
            class="checkbox checkbox--table"
            :class="{ 'checkbox--readonly': checked_after.length > 0 || (checked_before.length >= 2 && !checked_before.includes(row.id)) }">
            <input
              v-model="checked_before"
              type="checkbox"
              :value="row.id"
            />
            <span class="checkbox__checkmark"></span>
          </label>
        </template>
        <template #hotel="{ row }"><div class="table-default__left">{{ row.reservation.hotel.name }}</div></template>
        <template #check="{ row }">{{ dateFormat(row.check_out) }}</template>
        <template #name="{ row }"><div class="table-default__left table-default__controls">
        <span
          :class="`fflag ff-md fflag-${row.clients[0].citizenship ? row.clients[0].citizenship.code : 'empty'}`"
          v-tooltip="{ content: row.clients[0].citizenship ? row.clients[0].citizenship.name : 'Not provided', placement: 'top-start', offset: 10 }"
        ></span>
          <img v-if="row.clients[0].gender?.code === 'male'" src="@/assets/img/male-icon.svg" alt="">
          <img v-if="row.clients[0].gender?.code === 'female'" src="@/assets/img/female-icon.svg" alt="">
          <img v-if="!row.clients[0].gender" src="@/assets/img/gender-plug-icon.svg" alt="">
          <span>{{ row.clients[0].full_name }}</span>
          <span v-if="row.clients[0].age">({{ row.clients[0].age }} y.o.)</span>
        </div></template>
        <template #group="{ row }">
          <div class="table-default__controls table-default__left">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.clients[0].manager.crop.crop_url)" alt=""
                v-tooltip="{ content: row.clients[0].manager.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            <div class="table-default__left">{{ row.clients[0].group.name || '-' }}</div>
          </div>
        </template>
      </v-client-table>
      <app-cells><h2 class="title">After</h2></app-cells>
      <v-client-table
        :data="table_after.items"
        :columns="table_after.columns"
        :options="table_after.options"
        class="table-default"
      >
        <template #h__hotel><div class="table-default__left">Hotel</div></template>
        <template #h__name><div class="table-default__left">Full name</div></template>
        <template #h__group><div class="table-default__left">Group</div></template>
        <template #select="{ row }">
          <label
            class="checkbox checkbox--table"
            :class="{ 'checkbox--readonly': checked_before.length > 0 || (checked_after.length >= 2 && !checked_after.includes(row.id)) }">
            <input
              v-model="checked_after"
              type="checkbox"
              :value="row.id"
            />
            <span class="checkbox__checkmark"></span>
          </label>
        </template>
        <template #hotel="{ row }"><div class="table-default__left">{{ row.reservation.hotel.name }}</div></template>
        <template #check="{ row }">{{ dateFormat(row.check_in) }}</template>
        <template #name="{ row }"><div class="table-default__left table-default__controls">
        <span
          :class="`fflag ff-md fflag-${row.clients[0].citizenship ? row.clients[0].citizenship.code : 'empty'}`"
          v-tooltip="{ content: row.clients[0].citizenship ? row.clients[0].citizenship.name : 'Not provided', placement: 'top-start', offset: 10 }"
        ></span>
          <img v-if="row.clients[0].gender?.code === 'male'" src="@/assets/img/male-icon.svg" alt="">
          <img v-if="row.clients[0].gender?.code === 'female'" src="@/assets/img/female-icon.svg" alt="">
          <img v-if="!row.clients[0].gender" src="@/assets/img/gender-plug-icon.svg" alt="">
          <span>{{ row.clients[0].full_name }}</span>
          <span v-if="row.clients[0].age">({{ row.clients[0].age }} y.o.)</span>
        </div></template>
        <template #group="{ row }">
          <div class="table-default__controls table-default__left">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.clients[0].manager.crop.crop_url)" alt=""
                v-tooltip="{ content: row.clients[0].manager.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            <div class="table-default__left">{{ row.clients[0].group.name || '-' }}</div>
          </div>
        </template>
      </v-client-table>
    </template>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import {
  getClimbingGroupsBedInTwin,
  getClimbingGroupsJoinRoomingList,
  getClimbingsGroup,
  patchClimbingGroupsJoinRoomingList,
} from '@/http'

export default {
  name: 'ReservationsBedInTwinDetail',
  data() {
    return {
      page_loaded: false,
      group_detail: {},
      table_before: {
        items: [],
        columns: ['hotel', 'check', 'name', 'group'],
        options: {
          headings: {
            hotel: 'Hotel',
            check: 'Check-out',
            name: 'Full name',
            group: 'Group',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_after: {
        items: [],
        columns: ['hotel', 'check', 'name', 'group'],
        options: {
          headings: {
            hotel: 'Hotel',
            check: 'Check-in',
            name: 'Full name',
            group: 'Group',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_allocated: {
        items: [],
        columns: ['hotel', 'check_in', 'check_out', 'clients', 'verified', 'verified_by', 'created_by'],
        options: {
          headings: {
            hotel: 'Hotel',
            check_in: 'Check-in',
            check_out: 'Check-out',
            clients: 'Clients',
            verified: 'Verified',
            verified_by: 'Verified by',
            created_by: 'Created by',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_allocated_preloader: false,
    }
  },
  created() {
    Promise.all([getClimbingsGroup(Number(this.$route.params.id)), this.fetchTableBefore(), this.fetchTableAfter(), this.fetchTableAllocated()])
      .then(res => {
        this.group_detail = res[0].data
        this.page_loaded = true
      })
  },
  methods: {
    async fetchTableBefore() {
      const response = await getClimbingGroupsBedInTwin(Number(this.$route.params.id), { category: 'before' })
      this.table_before.items = response.data
    },
    async fetchTableAfter() {
      const response = await getClimbingGroupsBedInTwin(Number(this.$route.params.id), { category: 'after' })
      this.table_after.items = response.data
    },
    async fetchTableAllocated() {
      const response = await getClimbingGroupsJoinRoomingList({ climbing_group: Number(this.$route.params.id) })
      this.table_allocated.items = response.data
      this.table_allocated_preloader = false
    },
    onUpdateVerified(row) {
      this.table_allocated_preloader = true
      patchClimbingGroupsJoinRoomingList(row.id, { verified: row.verified })
        .then(() => {
          this.fetchTableAllocated()
          this.$notify({
            type: 'success',
            title: 'Changes saved',
          })
        })
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>
